import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { LayoutSingleColumn } from '../../components';
import { getSellerShopName } from '../../util/userHelpers';
import { fetchActiveSales } from '../../util/sales';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import StaticPage from '../PageBuilder/StaticPage';

import ShopCard from './ShopCard';
import { buildShopDirectory } from './ShopsPage.duck';
import css from './ShopsPage.module.css';

const searchableShopText = shop => {
  const profile = shop.seller.attributes.profile || {};
  const publicData = profile.publicData || {};
  const shopName = getSellerShopName(shop.seller, profile.displayName) || profile.displayName || '';
  const listingMetadata = shop.listings.flatMap(listing => {
    const data = listing.attributes?.publicData || {};
    return [
      listing.attributes?.title,
      data.listingType,
      data.genre,
      data.editionsource,
      data.merch_type,
    ];
  });
  return [
    shopName,
    profile.displayName,
    publicData.shopBio,
    ...(publicData.shopSpecialties || []),
    ...listingMetadata,
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase();
};

export const filterShops = (shops, keyword, province) => {
  const normalizedKeyword = keyword.trim().toLocaleLowerCase();
  return shops.filter(shop => {
    const publicData = shop.seller.attributes.profile?.publicData || {};
    const shopProvince = publicData.ships_from || publicData.province;
    return (
      (!normalizedKeyword || searchableShopText(shop).includes(normalizedKeyword)) &&
      (!province || shopProvince === province)
    );
  });
};

export const getShopProvinceOptions = (userFields, shops) => {
  const configuredProvinceField = userFields?.find(field =>
    ['ships_from', 'province'].includes(field.key)
  );
  const configuredProvinces = configuredProvinceField?.enumOptions || [];
  const loadedProvinces = [
    ...new Set(
      shops.map(shop => shop.seller.attributes.profile?.publicData?.province).filter(Boolean)
    ),
  ]
    .sort()
    .map(value => ({ option: value, label: value }));

  return configuredProvinces.length ? configuredProvinces : loadedProvinces;
};

export const getShopProvinceLabel = (provinceOptions, shop) => {
  const publicData = shop.seller.attributes.profile?.publicData || {};
  const value = publicData.ships_from || publicData.province;
  return provinceOptions.find(option => option.option === value)?.label || value || '';
};

const PROVINCES = [
  ['alberta', 'Alberta'],
  ['british-columbia', 'British Columbia'],
  ['manitoba', 'Manitoba'],
  ['new-brunswick', 'New Brunswick'],
  ['newfoundland-and-labrador', 'Newfoundland and Labrador'],
  ['nova-scotia', 'Nova Scotia'],
  ['ontario', 'Ontario'],
  ['prince-edward-island', 'Prince Edward Island'],
  ['quebec', 'Quebec'],
  ['saskatchewan', 'Saskatchewan'],
  ['northwest-territories', 'Northwest Territories'],
  ['nunavut', 'Nunavut'],
  ['yukon', 'Yukon'],
];

const specialtyLabels = {
  'special-editions': 'Special Editions',
  'collectors-editions': "Collector's Editions",
  'limited-editions': 'Limited Editions',
  fantasy: 'Fantasy',
  romance: 'Romance',
  mystery: 'Mystery & Thriller',
  thriller: 'Mystery & Thriller',
  'bookish-goods': 'Bookish Merch & Accessories',
  arcs: 'ARCs / Advance Copies',
  'indie-small-press': 'Indie & Small Press',
  'vintage-books': 'Vintage Books',
  'childrens-books': "Children's Books",
  'manga-graphic-novels': 'Manga & Graphic Novels',
  'handmade-bookish-merch': 'Handmade Bookish Merch',
  'book-accessories': 'Book Accessories',
  'art-stickers-stationery': 'Art Prints, Stickers & Stationery',
};

export const getSpecialtyLabel = specialty =>
  specialtyLabels[String(specialty).toLowerCase()] ||
  String(specialty)
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const ShopsPageComponent = ({ listings, reviewsByUserId, error }) => {
  const [keyword, setKeyword] = useState('');
  const [province, setProvince] = useState('');
  const [saleOnly, setSaleOnly] = useState(false);
  const [bundleOnly, setBundleOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const shops = useMemo(() => Object.values(buildShopDirectory(listings, reviewsByUserId)), [
    listings,
    reviewsByUserId,
  ]);
  useEffect(() => {
    fetchActiveSales(listings.map(listing => listing.id.uuid)).then(setSales);
  }, [listings]);
  const provinces = useMemo(() => PROVINCES.map(([option, label]) => ({ option, label })), []);
  const filteredShops = useMemo(() => {
    const salesByListing = new Set(sales.map(sale => sale.listingId));
    const result = filterShops(shops, keyword, province).filter(shop => {
      const profileData = shop.seller.attributes.profile?.publicData || {};
      const shopHasSale = shop.listings.some(listing => salesByListing.has(listing.id.uuid));
      return (!saleOnly || shopHasSale) && (!bundleOnly || profileData.bundleDiscountEnabled);
    });
    return result;
  }, [bundleOnly, keyword, province, saleOnly, sales, shops]);

  return (
    <StaticPage title="PageHop Shops" description="Browse PageHop sellers and their published listings.">
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root} id="main-content">
          <header className={css.pageHeader}>
            <h1>Explore Shops on PageHop</h1>
            <p>
              Every PageHop shop belongs to another reader. Browse books and bookish merch, bundle
              from the same seller, and discover shops offering discounts or sales.
            </p>
            <form
              className={css.shopSearchForm}
              role="search"
              onSubmit={event => event.preventDefault()}
            >
              <div className={css.shopSearchRow}>
                <input
                  className={css.shopSearchInput}
                  type="search"
                  value={keyword}
                  onChange={event => setKeyword(event.target.value)}
                  placeholder="Search shops"
                  aria-label="Search shops"
                />
                <button type="submit" className={css.shopSearchButton}>
                  Search
                </button>
                <button
                  type="button"
                  className={css.shopFiltersButton}
                  onClick={() => setFiltersOpen(current => !current)}
                  aria-expanded={filtersOpen}
                >
                  Filters
                </button>
              </div>
              {filtersOpen ? (
                <div className={css.shopFiltersPanel} aria-label="Shop filters">
                  <label className={css.filterLabel}>
                    <span>Province</span>
                    <select
                      className={css.filterControl}
                      value={province}
                      onChange={event => setProvince(event.target.value)}
                    >
                      <option value="">All Provinces</option>
                      {provinces.map(({ option, label }) => (
                        <option key={option} value={option}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={css.filterLabel}>
                    <span>Shop filters</span>
                    <select
                      className={css.filterControl}
                      value={saleOnly ? 'sale' : bundleOnly ? 'bundle' : ''}
                      onChange={event => {
                        setSaleOnly(event.target.value === 'sale');
                        setBundleOnly(event.target.value === 'bundle');
                      }}
                    >
                      <option value="">All shops</option>
                      <option value="sale">Has a Sale</option>
                      <option value="bundle">Bundle Discount</option>
                    </select>
                  </label>
                </div>
              ) : null}
            </form>
          </header>
            <section className={css.contentBand} aria-label="Shop directory">
              <div className={css.contentInner}>
              <p className={css.shopCount}>Showing {filteredShops.length} PageHop Shops</p>
                {error ? (
                  <p className={css.error} role="alert">
                    Unable to load shops right now.
                  </p>
                ) : null}
                {filteredShops.length ? (
                  <div className={css.shopGrid}>
                    {filteredShops.map(shop => (
                      <ShopCard
                        key={shop.seller.id.uuid}
                        shop={shop}
                        provinceLabel={getShopProvinceLabel(provinces, shop)}
                        sales={sales}
                        getSpecialtyLabel={getSpecialtyLabel}
                      />
                    ))}
                  </div>
                ) : (
                  <p className={css.emptyState} role="status">
                    No shops match those filters yet.
                  </p>
                )}
              </div>
            </section>
        </main>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

const mapStateToProps = state => ({
  listings: state.ShopsPage?.listings || [],
  reviewsByUserId: state.ShopsPage?.reviewsByUserId || {},
  error: state.ShopsPage?.error,
});

export { ShopsPageComponent };
export default connect(mapStateToProps)(ShopsPageComponent);
